import { useEffect, useMemo } from 'react'

/** Visible stretches shorter than this are scroll-throughs, not attention — not reported. */
export const DWELL_MIN_MS = 1000

/**
 * Watches a set of elements at ≥ 30 % visibility and reports two things:
 * - `onView(el)` ONCE per element, the first time it comes into view (reach), and
 * - `onDwell(el, ms)` every time it LEAVES view (or the tab hides / the page unmounts),
 *   with the milliseconds it was visible in that stretch — attention. A hidden tab pauses
 *   the clock; several stretches on one element report separately (sum them in GA4).
 *
 * `key` must change when the observed set changes (a new product, a new page of rows) so
 * the seen-set resets with it. The unmount flush covers client-side navigation; the
 * `pagehide` flush covers tab close, best-effort — the listener must push to the dataLayer
 * synchronously (see app-events-listeners-sg.ts), so the flush lands before the page goes.
 *
 * Shared by the DealHunt catalog surfaces (`apps/sound-guys/src/lib/analytics/catalog.tsx`)
 * and the "Have your say" section (`comments/conversation/HaveYourSay.tsx`).
 */
export function useVisibility(
  key: string,
  resolve: () => HTMLElement[],
  handlers: { onView: (el: HTMLElement) => void; onDwell: (el: HTMLElement, ms: number) => void }
): void {
  const seen = useMemo(() => new Set<Element>(), [key]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return undefined
    }
    const els = resolve()
    if (!els.length) {
      return undefined
    }
    const inView = new Set<Element>()
    const since = new Map<Element, number>()
    const now = (): number => performance.now()
    const start = (el: Element, t: number): void => {
      if (document.visibilityState === 'visible' && !since.has(el)) {
        since.set(el, t)
      }
    }
    const flush = (el: Element, t: number): void => {
      const from = since.get(el)
      since.delete(el)
      if (from === undefined) {
        return
      }
      const ms = Math.round(t - from)
      if (ms >= DWELL_MIN_MS) {
        handlers.onDwell(el as HTMLElement, ms)
      }
    }
    const flushAll = (): void => {
      const t = now()
      Array.from(since.keys()).forEach(el => flush(el, t))
    }

    const io = new IntersectionObserver(
      entries => {
        const t = now()
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            inView.add(entry.target)
            if (!seen.has(entry.target)) {
              seen.add(entry.target)
              handlers.onView(entry.target as HTMLElement)
            }
            start(entry.target, t)
          } else {
            inView.delete(entry.target)
            flush(entry.target, t)
          }
        })
      },
      { threshold: 0.3 }
    )
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        const t = now()
        inView.forEach(el => start(el, t))
      } else {
        flushAll()
      }
    }
    els.forEach(el => io.observe(el))
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', flushAll)
    return () => {
      flushAll()
      window.removeEventListener('pagehide', flushAll)
      document.removeEventListener('visibilitychange', onVisibility)
      io.disconnect()
    }
    // `resolve`/`handlers` are re-created per render by design; `key` is the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, seen])
}
